import { Button, IconButton, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography } from '@material-ui/core';
import {AddCircle, Edit, Delete, VisibilityOff, Visibility, Fingerprint} from '@material-ui/icons';
import React, {useEffect, useRef, useState} from 'react';
import { useHistory } from 'react-router-dom';
import Group from '../../../Domain/Models/Group';
import { groupRepository } from '../../../Domain/Repositories/GroupRepository';
import DecisionModal from "../../UiComponents/DecisionModal";
import ErrorCardController from "../../UiComponents/ErrorCardController";
import Loading from "../../UiComponents/Loading";
import {CompletedCubeService} from "../../../Domain/Services/CompletedCubeService";
import GroupModal from "./GroupModal";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  title: {
    flexGrow: 1
  },
  clickableCell: {
    cursor: 'pointer',
  },
}));

export default function GroupList()
{
  const classes = useStyles();
  const history = useHistory();

  const errorCardControllerRef = useRef<ErrorCardController>(null);

  const [idVisible, setIdVisible] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<Group>();
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompletedGroups, setShowCompletedGroups] = useState(true);

  const [time, setTime] = useState(Date.now());

  const getTotalTimeInVenueSeconds = (group: Group): number =>
  {
    const now = new Date();
    const entryTime = new Date(group.createdAt);
    const seconds = (now.getTime() - entryTime.getTime()) / 1000;

    return seconds;
  }

  const getAvgTimePerCubeSeconds = (group: Group): number =>
  {
    const totalTime = getTotalTimeInVenueSeconds(group);
    const completedCubeCount = CompletedCubeService.getNumberOfCompletedCubes(group);

    if (completedCubeCount === 0)
    {
      return 0;
    }

    return totalTime / completedCubeCount;
  }

  const formatTime = (sceonds: number): string =>
  {
    const h = Math.floor(sceonds / 3600);
    const m = Math.floor(sceonds % 3600 / 60);
    const s = Math.floor(sceonds % 3600 % 60);

    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  const handleGroupClicked = (group: Group) =>
  {
    history.push(`/group/${group.id}`);
  };
  
  const handleEditGroupClicked = (group: Group) =>
  {
    setGroupToEdit(group);
    setEditDialogOpen(true);
  };

  const handleDeleteGroupClicked = (group: Group) =>
  {
    setGroupToEdit(group);
    setDecisionDialogOpen(true);
  }

  const handleDeleteDecisionAccepted = () =>
  {
    handleDialogClose();
    setLoading(true);

    if (groupToEdit === undefined)
    {
      return;
    }

    groupRepository.delete(groupToEdit)
        .then(() => setLoading(false))
        .catch((e: Error) =>
        {
          setLoading(false);
          errorCardControllerRef.current?.addErrorMessage(e.message)
        });
  }

  const handleDialogClose = () =>
  {
    setDecisionDialogOpen(false);
    setEditDialogOpen(false);
    setGroupToEdit(undefined);
  };
  
  useEffect(() =>
  {
    setLoading(true);
    groupRepository.load()
        .then(() => setLoading(false))
        .catch((e: Error) =>
        {
          setLoading(false);
          errorCardControllerRef.current?.addErrorMessage(e.message);
        })

    const interval = setInterval(() => setTime(Date.now()), 5000);
    return () => {
      clearInterval(interval);
    };
  }, [])

  return (
      <>
        <DecisionModal
            open={decisionDialogOpen}
            message={`Are you sure you want to delete ${groupToEdit?.id}?`}
            onAccept={handleDeleteDecisionAccepted}
            onCancel={handleDialogClose}
        />

        {groupToEdit && (
          <GroupModal
            open={editDialogOpen}
            onClose={handleDialogClose}
            group={groupToEdit}
            />
        )}

        <Loading isLoading={loading}>
          <ErrorCardController ref={errorCardControllerRef}/>

          <Paper className={classes.paper}>
            <Toolbar>
              <Typography className={classes.title} component="h2" variant="h6" color="primary" gutterBottom>
                Groups
              </Typography>
              <IconButton size="small" color="default" onClick={() => setIdVisible(!idVisible)}>
                { idVisible ? <VisibilityOff/> : <Visibility/> }
              </IconButton>
              <Button onClick={() => setShowCompletedGroups(!showCompletedGroups)}
                      color="primary"
                      variant="contained"
              >
                {showCompletedGroups ? "Hide Completed" : "Show Completed"}
            </Button>
            </Toolbar>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {idVisible && <TableCell>ID</TableCell>}
                    <TableCell>Name</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Subtitles</TableCell>
                    <TableCell>Corporate</TableCell>
                    <TableCell>Num. Completed Cubes</TableCell>
                    <TableCell>Total Time in Venue</TableCell>
                    <TableCell>Avg. Time per Cube</TableCell>
                    <TableCell/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupRepository.groups
                    .filter((x) => {
                      if (showCompletedGroups)
                      {
                        return true;
                      }
                      
                      return !x.complete;
                    })
                    .sort((a, b) => getAvgTimePerCubeSeconds(b) - getAvgTimePerCubeSeconds(a))
                    .map((row) => (
                      <TableRow key={row.id} hover>
                        {idVisible && <TableCell scope="row">{row.id}</TableCell>}
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleGroupClicked(row)}
                        >
                          {row.playerPairs.map(x => x.name).join(' vs. ')}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleGroupClicked(row)}
                        >
                          {row.currentPhase.toString()}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleGroupClicked(row)}
                        >
                          {row.enableSubtitles ? 'Enabled' : 'Disabled'}
                        </TableCell>
                        <TableCell
                          className={classes.clickableCell}
                          onClick={() => handleGroupClicked(row)}
                        >
                          {row.isCorporateGroup ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleGroupClicked(row)}
                        >
                          {CompletedCubeService.getNumberOfCompletedCubes(row)}
                        </TableCell>
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleGroupClicked(row)}
                        >
                          {formatTime(getTotalTimeInVenueSeconds(row))}
                        </TableCell>
                        <TableCell
                            className={classes.clickableCell}
                            onClick={() => handleGroupClicked(row)}
                        >
                          {formatTime(getAvgTimePerCubeSeconds(row))}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="default" onClick={() => handleEditGroupClicked(row)}>
                            <Edit/>
                          </IconButton>
                          <IconButton size="small" color="default" onClick={() => handleDeleteGroupClicked(row)}>
                            <Delete/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          </Paper>
        </Loading>
      </>
  );
}